/* TrackerBase Administration: State-Office-Election Menu
 * 
 * Angular module: "trackerbase-app.directives.state-office-election-menu"
 * Controller: (not exposed: StateOfficeElectionMenuController)
 * Controller instance name in template: tbStOffElMenuCtrl
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import Assert from "tbAssert";

import ngSanitize from "angular-sanitize";
import ngAria from "angular-aria";
import ngAnimate from "angular-animate";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import AllStatesServiceModule from "../../services/AllStatesService";
import StateDataModule from "../../services/StateDataService";
import OfficeDataModule from "../../services/OfficeDataService";
import OfficeDisplayNameFilterModule from "../../filters/OfficeDisplayNameFilter";


function getItemType(item) {
    if (item == null) {
        return undefined;
    }

    if (Number.isInteger(item.stateId))
    {
        return "State";
    }

    if (Number.isInteger(item.officeId))
    {
        return "Office";
    }

    if (Number.isInteger(item.electionId))
    {
        return "Election";
    }

    return undefined;
}

function readCheckBoxOptionValue(cBoxOptionValue) {
    if (typeof(cBoxOptionValue) === "boolean") {
        return cBoxOptionValue;
    }

    return (cBoxOptionValue === "true");
}

const mScopeSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mAllStatesSvc = new WeakMap();
const mStateDataSvc = new WeakMap();
const mOfficeDataSvc = new WeakMap();

const mElectionDataSource = new WeakMap();
const mOfficeDataSource = new WeakMap();
const mSubTreeDataSource = new WeakMap();

const mCheckBoxesEnabled = new WeakMap();
const mOnSelectedItemsFn = new WeakMap();
const mOnCheckedItemsFn = new WeakMap();

const mSubscriptionTreeOptions = new WeakMap();
const mSelectedItems = new WeakMap();
const mInitComplete = new WeakMap();


class StateOfficeElectionMenuComponentController {
    constructor($scope, $log, AllStatesService, StateData, OfficeData) {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Controller Scope Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert(AllStatesService != null, "Assertion Failure: Missing All States Service Dependency");
        Assert.StrictAssert(StateData != null, "Assertion Failure: Missing State Data Service Dependency");
        Assert.StrictAssert(OfficeData != null, "Assertion Failure: Missing Office Data Service Dependency");

        mInitComplete.set(this, false);

        mScopeSvc.set(this, $scope);
        mLogSvc.set(this, $log);
        mAllStatesSvc.set(this, AllStatesService);
        mStateDataSvc.set(this, StateData);
        mOfficeDataSvc.set(this, OfficeData);

        const logSvc = mLogSvc.get(this);

        mElectionDataSource.set(this, {
            transport: {
                read: (options) => {

                    mOfficeDataSvc.get(this)
                        .get({officeId: options.data.officeId})
                        .$promise
                        .then(
                            (officeDataResult) => {
                                logSvc.debug("Subscription Tree Third-Level (Election) Data Source Read Success");
                                options.success(officeDataResult.result.officeElections);
                            },
                            (errorResponse) => {
                                logSvc.error("Subscription Tree Third-Level (Election) Data Source Read Failure");
                                options.error(errorResponse);
                            }
                        );
                }
            },
            schema: {
                model: {
                    id: "electionId",
                    fields: {
                        electionId: {
                            from: "electionId",
                            type: "number"
                        },
                        fullName: {
                            from: "electionName"
                        },
                        electionDate: {
                            from: "electionDate",
                            type: "date"
                        }
                    },
                    hasChildren: false
                }
            }
        });

        mOfficeDataSource.set(this, {
            transport: {
                read: (options) => {

                    mStateDataSvc.get(this)
                        .get({stateId: options.data.stateId})
                        .$promise
                        .then(
                            (stateDataResult) => {
                                logSvc.debug("Subscription Tree Second-Level (State Office) Data Source Read Success");
                                options.success(stateDataResult.result.stateOffices);
                            },
                            (errorResponse) => {
                                logSvc.error("Subscription Tree Second-Level (State Office) Data Source Read Failure");
                                options.error(errorResponse);
                            }
                        );
                }
            },
            schema: {
                model: {
                    id: "officeId",
                    fields: {
                        officeId: {
                            from: "officeId",
                            type: "number"
                        },
                        fullName: {
                            from: "officeName"
                        },
                        officeType: {
                            from: "officeType"
                        },
                        officeDistrict: {
                            from: "officeDistrict"
                        }
                    },
                    hasChildren: true,
                    children: mElectionDataSource.get(this)
                }
            }
        });

        mSubTreeDataSource.set(this, new kendo.data.HierarchicalDataSource({
            transport: {
                read: (options) => {
                    mAllStatesSvc.get(this)
                        .GetStatesAsync()
                        .then(
                            (statesData) => {
                                logSvc.debug("Subscription Tree Top-Level Data Source Read Success");
                                options.success(statesData);
                            },
                            (errorResponse) => {
                                logSvc.error("Subscription Tree Top-Level Data Source Read Failure");
                                options.error(errorResponse);
                            }
                        );
                }
            },
            schema: {
                model: {
                    id: "stateId",
                    fields: {
                        stateId: {
                            from: "stateId",
                            type: "number"
                        },
                        fullName: {
                            from: "fullName"
                        }
                    },
                    hasChildren: true,
                    children: mOfficeDataSource.get(this)
                }
            }
        }));

        //We'll initialize this in $onInit()
        mSubscriptionTreeOptions.set(this, null);

        mSelectedItems.set(this, {
            data: null,
            dataItem: null
        });

        $scope.$on("$destroy", function() {
            let menuRootElement = angular.element("#tbStOffElMenuRoot");
            kendo.destroy(menuRootElement);
        });

    }

    $onInit() {
        mLogSvc.get(this).debug(`OnInit: About to create tbSubscriptionTreeOptions. Use Check Box Value is ${mCheckBoxesEnabled.get(this) === true ? "true" : "false"}`);

        //Angular does not set variables involved in component binding until after the constructor, but calls $onInit after they are set.
        mSubscriptionTreeOptions.set(this, {
            checkboxes: mCheckBoxesEnabled.get(this),
            dataSource: mSubTreeDataSource.get(this),
            dataTextField: "fullName"
        });

        //Reload kendo tree widget with options we just set.
        mLogSvc.get(this).debug("OnInit: Setting mInitComplete to true.");
        mInitComplete.set(this, true);
    }

    //Kendo widget will reload options (from tbSubscriptionTreeOptions) when this changes.
    get initComplete() {
        return mInitComplete.get(this);
    }

    set initComplete(updatedVal) {
        mInitComplete.set(this, updatedVal);
    }

    get useCheckBoxes() {
        return (mCheckBoxesEnabled.get(this) ? "true" : "false");
    }

    set useCheckBoxes(updatedVal) {
        mCheckBoxesEnabled.set(this, readCheckBoxOptionValue(updatedVal));
    }

    get tbSubscriptionTreeOptions() {
        return mSubscriptionTreeOptions.get(this);
    }

    set tbSubscriptionTreeOptions(updatedVal) {
        mSubscriptionTreeOptions.set(this, updatedVal);
    }

    get onCheckedItems() {
        return mOnCheckedItemsFn.get(this);
    }

    set onCheckedItems(updatedFn) {
        mOnCheckedItemsFn.set(this, updatedFn);
    }

    get onSelectedItem() {
        return mOnSelectedItemsFn.get(this);
    }

    set onSelectedItem(updatedFn) {
        return mOnSelectedItemsFn.set(this, updatedFn);
    }

    get selectedItems() {
        return mSelectedItems.get(this);
    }

    set selectedItems(updatedVal) {
        mSelectedItems.set(this, updatedVal);
    }

    handleMenuItemChange(d, dI, kE) {
        mLogSvc.get(this).debug("In Handle Menu Item Change");

        const currentSelectedItems = this.selectedItems;
        currentSelectedItems.data = d;
        currentSelectedItems.dataItem = dI;
        angular.noop(kE);

        this.onSelectedItem({
            selectedMenuItem: currentSelectedItems.dataItem,
            selectedMenuItemType: getItemType(currentSelectedItems.dataItem)
        });
    }

    handleMenuItemCheck(kE) {
        mLogSvc.get(this).debug("In Handle Menu Item Check");

        let checkedDataItem = mScopeSvc.get(this).tbStOffElMenu.dataItem(kE.node);

        this.onCheckedItems({
            updatedItem: checkedDataItem,
            updatedItemType: getItemType(checkedDataItem)
        });
    }

}

StateOfficeElectionMenuComponentController.$inject = [
    "$scope",
    "$log",
    "AllStatesService",
    "StateData",
    "OfficeData"
];

StateOfficeElectionMenuComponentController.controllerAs = "tbStOffElMenuCtrl";
StateOfficeElectionMenuComponentController.template = require("./StateOfficeElectionMenu.tpl.html");

StateOfficeElectionMenuComponentController.dependencies = [
    ngSanitize,
    ngAria,
    ngAnimate,
    kendoDirectivesModule,
    AllStatesServiceModule,
    StateDataModule,
    OfficeDataModule,
    OfficeDisplayNameFilterModule
];

export default StateOfficeElectionMenuComponentController;



//////////////////
// WEBPACK FOOTER
// ./src/directives/StateOfficeElectionMenu/StateOfficeElectionMenuController.js
// module id = QEFt
// module chunks = 1